import React from 'react';

import {
  Title,
  Paper,
  Stack,
  Alert,
  Button,
  Group
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useParams, useNavigate } from 'react-router-dom';

import ClientForm, { type ClientFormData } from '../components/client/ClientForm';
import { type Client } from '../components/client/types';
import ErrorState from '../components/state/ErrorState';
import LoadingState from '../components/state/LoadingState';
import { usePostJSON } from '../utils/request';

interface ClientResponse {
  success: boolean;
  client: Client;
}

const EditClient: React.FC = () => {
  const { id: _id } = useParams<{ id: string }>();
  const id = _id ? parseInt(_id, 10) : undefined;
  const navigate = useNavigate();

  // Fetch client data using the /data_one endpoint
  const { data: clientResponse, error, loading } = usePostJSON<ClientResponse>(
    '/api/client/data_one',
    { id },
    [id]
  );

  const client = clientResponse?.client || null;

  // Handle missing ID
  if (!id) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Client</Title>
        <Paper withBorder p="md">
          <ErrorState
            message="Client ID is required"
            style={{ minHeight: '200px' }}
            title="Invalid Client ID"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/clients')}>
              Back to Clients
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  const handleSuccess = () => {
    notifications.show({
      title: 'Success',
      message: 'Client updated successfully',
      color: 'green'
    });
  };

  if (loading) {
    return (
      <LoadingState 
        message="Loading client data..." 
        style={{ height: '50vh' }} 
      />
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Client</Title>
        <Paper withBorder p="md">
          <ErrorState
            message={error.message}
            style={{ minHeight: '200px' }}
            title="Error Loading Client"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/clients')}>
              Back to Clients
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (!client) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Client</Title>
        <Paper withBorder p="md">
          <Alert color="red" title="Client Not Found">
            The requested client could not be found.
          </Alert>
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/clients')}>
              Back to Clients
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  const formData: ClientFormData & { id: number; carePlan?: string } = {
    id: client.id,
    firstName: client.firstName,
    lastName: client.lastName,
    phone: client.phone || '',
    email: client.email || '',
    dob: client.dob || '',
    carePlan: client.carePlan || '',
    tags: client.tags || [],
    externalSystem: client.externalSystem || '',
    externalId: client.externalId || '',
  };

  return (
    <Stack gap="md">
      <Group align="center" justify="space-between">
        <Title order={1}>Edit Client</Title>
        <Button variant="outline" onClick={() => void navigate('/clients')}>
          Back to All Clients
        </Button>
      </Group>

      <Paper withBorder p="xl">
        <Stack gap="lg">
          <ClientForm
            initialData={formData}
            mode="edit"
            onSuccess={handleSuccess}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default EditClient;
